/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FieldMaker } from '@src/app/shared/field/FieldMaker';
import { FieldSet, FieldSetComponent } from '@src/app/shared/form/field-set/field-set.component';
import { FormTextAreaComponent } from '@src/app/shared/form/form-text-area/form-text-area.component';
import { minChars, minWords, required } from '@src/app/shared/validators';
import { FormDateComponent } from '@src/app/shared/form/form-date/form-date.component';
import { UserService } from '@src/app/modules/user/user.service';
import { FormTextComponent } from '@src/app/shared/form/form-text/form-text.component';
import { RequestService } from '@src/app/modules/crm/request.service';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { ServiceRequest } from '@src/app/model/serviceRequest';
import { MatButtonModule } from '@angular/material/button';
import { CurrentUserService } from '@src/app/modules/user/current-user.service';
import { AbstractObject } from '@src/app/model/abstract-object';
import { Field } from '@src/app/shared/field/Field';

@Component({
    selector: 'app-simple-request',
    imports: [FieldSetComponent, MatDialogModule, MatButtonModule],
    templateUrl: './simple-request.component.html',
    styleUrl: './simple-request.component.scss'
})
export class SimpleRequestComponent {

    private dialogRef = inject(MatDialogRef<SimpleRequestComponent>);
    public data: Partial<ServiceRequest> = inject(MAT_DIALOG_DATA);

    teamUserSvc = inject(UserService);
    requestSvc = inject(RequestService);
    currentUserSvc = inject(CurrentUserService);


    titleField = FormTextComponent.make('Title - A shortname for this request', 'title',
        { validators: [required, minChars(10)]}
    );
    descField = FormTextAreaComponent.make('Description', 'description',
        { validators: [required, minWords(3)]}
    );

    priorityField = ServiceRequest.getPriorityField().override({ formColumn: 2 });
    plannedDate = FormDateComponent.make('Planned Date', 'plannedDate').override({ formColumn: 2 });
    //ownedByField = FormPicklistComponent.make('Owned By', 'ownedById', 'ownedBy', { }).override({ formColumn: 2 });
    ownedByField = ServiceRequest.makeOwnedByField().override({ formColumn: 2 });
    sourceField = ServiceRequest.getSourcePicklist().override({ formColumn: 2, visible: Field.noShow });

    ready = false;

    fieldSet = new FieldSet({
        fields: [
            FieldMaker.id(),
            FieldMaker.rev(),
            FieldMaker.idHolder('teamId'),
            FieldMaker.idHolder('omcId'),
            FieldMaker.idHolder('agentTeamId'),
            FieldMaker.idHolder('ballots'),
            FieldMaker.idHolder('forumId'),
            this.titleField,
            this.descField,
            this.plannedDate,
            this.ownedByField,
            this.priorityField,
            this.sourceField,
        ], formLayout: [{ cells: [{ width: '90%' }, { width: '10%' }] }],
    })

    constructor() {
        //this.teamUserSvc.getAgentUsers().subscribe( users => this.ownedByField.setPicklistItems(users));
        this.currentUserSvc.getCurrentUser().pipe(takeUntilDestroyed()).subscribe( x => {
            if (!this.data.id) {
                const sr = new ServiceRequest();
                sr.teamId = x.currentTeam.id;
                sr.omcId = x.inOmc.id;
                sr.agentTeamId = x.inOmc.teamId;
                sr.ownedById = x.id;
                sr.ownedBy = x;
                Object.assign(this.data, sr);
            }
            this.fieldSet.setValue(this.data as AbstractObject);
            this.ready = true
        })
    }

    save() {
        if (this.data.id) {
            this.requestSvc.put(this.fieldSet.getFormValue()).subscribe(updatedSR => {
                this.dialogRef.close(updatedSR);
            })
        } else {
            this.requestSvc.post(this.fieldSet.getFormValue()).subscribe(newSR => {
                this.dialogRef.close(newSR);
            })
        }
    }
}
